import React, { useEffect, useMemo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table'
import { useSelector } from 'react-redux'
import { Edit, Trash2, TagIcon, Users, Folder } from 'lucide-react'
import { RootState } from 'constants/interfaces'
import { Table, TableContainer } from '../commonUI/Table/styles'
import { Pagination } from '../commonUI/Table/Pagination'
import { PreviewAvatarsList } from 'components/CommonComponents/PreviewList'
import { ActionMenu } from '../commonUI/ActionMenu'
import { TAG_TYPE } from './TagModal'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'
import { isRtlSelector } from 'redux/selectors/general'

const createColumns = (
  optionsList: { label: string; icon: React.ReactNode; onClick: (sk: string) => void; id: string }[],
  loadingTags: Record<string, boolean>
) => [
  {
    Header: () => (
      <Text>
        <LocaleText text="name" isLocalUse />
      </Text>
    ),
    accessor: 'name',
    sortType: 'alphanumeric',
    Cell: ({ row }: { row: { original: { name: string; tag_color: string } } }) => {
      const tagColor = row.original.tag_color
      const bgColorWithOpacity = `${tagColor}25`
      return (
        <Flex alignItems="center" style={{ columnGap: '10px' }}>
          <Flex p="5px" borderRadius="full" bg={bgColorWithOpacity} justify="center" align="center">
            <TagIcon color={tagColor} size={16} />
          </Flex>
          <Text fontSize="14px" fontWeight="bold">
            {row.original.name}
          </Text>
        </Flex>
      )
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="participants" />
      </Text>
    ),
    accessor: 'audience',
    Cell: ({ row }: { row: { original: { users: string[]; groups: string[] } } }) => {
      const { retailUsersObject, groups: retailGroupsObject } = useSelector((state: RootState) => state.config.config)
      const t_users = useLocaleText('users')
      const t_groups = useLocaleText('groups')

      const tagUsers = row.original.users ? row.original.users.map((user) => retailUsersObject?.[user]) : []
      const tagGroups = row.original.groups ? row.original.groups.map((group) => retailGroupsObject?.[group]) : []

      return (
        <Box>
          <PreviewAvatarsList
            icon={Users}
            label={t_users}
            items={tagUsers.map((user) => ({
              name: `${user?.first_name} ${user?.last_name || ''}`,
              profile_img_url: user?.profile_img_url,
            }))}
            defaultValue="-"
            max={4}
          />

          <PreviewAvatarsList
            icon={Folder}
            label={t_groups}
            items={tagGroups.map((group) => ({
              name: group?.name || '',
            }))}
            isGroup
            max={4}
            defaultValue="-"
          />
        </Box>
      )
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_actions" />
      </Text>
    ),
    accessor: 'actions',
    Cell: ({ row }: { row: { original: { sk: string; tag_type: number } } }) => {
      const tag = row.original

      const isSystemTag = tag.tag_type === TAG_TYPE.SYSTEM || tag.tag_type === undefined

      const filteredOptionsList = optionsList.filter((opt) => !(opt.id === 'delete' && isSystemTag))

      const adaptedOptionsList = filteredOptionsList.map((option) => ({
        ...option,
        onClick: () => option.onClick(tag.sk),
      }))

      return (
        <Flex w="100%" justifyContent="flex-start">
          <ActionMenu
            isLoading={loadingTags[tag.sk] || false}
            tooltipText="Edit Tag"
            optionsList={adaptedOptionsList}
            triggerProps={{ top: 0, right: 0, pos: 'relative' }}
          />
        </Flex>
      )
    },
  },
]

interface TagsTableProps {
  onEditTag: (sk: string) => void
  onDeleteTag: (sk: string) => void
  searchQuery: string
  loadingTags: Record<string, boolean>
}

export const TagsTable: React.FC<TagsTableProps> = ({ onEditTag, onDeleteTag, searchQuery, loadingTags }) => {
  const { tags } = useSelector((state: RootState) => state.config.config)
  const isRtl = useSelector(isRtlSelector)
  const optionsList = useMemo(
    () => [
      { label: 'admin_edit_action', icon: <Edit size={16} />, onClick: (sk: string) => onEditTag(sk), id: 'edit' },
      {
        label: 'admin_delete_action',
        icon: <Trash2 size={16} />,
        onClick: (sk: string) => onDeleteTag(sk),
        color: 'red.500',
        id: 'delete',
      },
    ],
    [onDeleteTag, onEditTag]
  )

  const columns = useMemo(() => createColumns(optionsList, loadingTags), [optionsList, loadingTags])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: tags,
      initialState: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    setGlobalFilter(searchQuery)
  }, [searchQuery, setGlobalFilter])

  return (
    <Box>
      <TableContainer>
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any, index: number) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column: any, index: number) => (
                  <th {...column.getHeaderProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: any, index: number) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell: any, index: number) => (
                    <td {...cell.getCellProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableContainer>

      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </Box>
  )
}
